<template>
  <AddonCard
    :data-change="changeIndex"
    data-test-id="addon-sms"
    :title="'Mobile SMS addon'"
    :subtitle="'Receive text messages using real phone numbers.'"
    icon="sms"
    :loading="loading"
    addon-id="sms"
    :data-has-button="hasSubscriptionAndNoAddon ? '1' : '0'"
    :data-bad-currency-or-schedule="!productPhonePlans ? '1' : '0'"
    :no-footer="!hasSubscriptionAndNoAddon"
  >
    <AddonTable
      v-if="productPhonePlans"
      :headers="['Country', 'Phone rental', 'SMS cost', 'Reply cost']"
    >
      <Tr v-for="(price, i) in products" :key="`${i}_${price.priceId}`">
        <Td>
          <PhoneCountryFlag :phone-country="price.phoneCountry" />
        </Td>
        <Td>
          <TablePrice
            v-if="getPhonePrice(price)"
            :symbol="selectedCurrencySymbol"
            :currency="selectedCurrency"
            :price="getPhonePrice(price)"
            :text="'per number per month'"
          />
        </Td>
        <Td>
          <TablePriceUnit
            v-if="price"
            :multiplier="1"
            :symbol="selectedCurrencySymbol"
            :currency="selectedCurrency"
            :unit-price="price.perUnitCost"
            :text="'per SMS received'"
          />
        </Td>
        <Td>
          <TablePriceUnit
            v-if="price"
            :symbol="selectedCurrencySymbol"
            :currency="selectedCurrency"
            :multiplier="price.replyMultiplier"
            :unit-price="price.perUnitCost"
            :text="'per SMS reply'"
          />
        </Td>
      </Tr>
    </AddonTable>
    <AddonNotSupported v-else />
    <template #footer>
      <UpgradeButtonAddPhonePlan v-if="hasSubscriptionAndNoAddon" expedite />
    </template>
  </AddonCard>
</template>

<script setup lang="ts">
import PhoneCountryFlag from '../../sms/PhoneCountryFlag.vue';
import AddonNotSupported from '../AddonNotSupported.vue';
import { type StripePhonePlanProductItem } from '@mailslurp/admin-sdk';
import TablePrice from '../../pricing/TablePrice.vue';
import TablePriceUnit from '../../pricing/TablePriceUnit.vue';
import { onMounted, ref } from 'vue';
import { useStore3 } from '../../../store/main';
import { storeToRefs } from 'pinia';
import Tr from '../../table/parts/Tr.vue';
import Td from '../../table/parts/Td.vue';
import AddonCard from '../AddonCard.vue';
import UpgradeButtonAddPhonePlan from '../../pricing/addon/UpgradeButtonAddPhonePlan.vue';
import { loadFetchMany } from '../../../lib/services/helpers';
import AddonTable from '../AddonTable.vue';

const store3 = useStore3();

const loading = ref<boolean>(false);
const {
  subscription,
  phonePlans,
  productPhonePlans,
  productPhoneNumbers,
  selectedCurrency,
  selectedCurrencySymbol,
  selectedSchedule,
} = storeToRefs(store3);

defineProps<{
  changeIndex: number;
}>();

const products = computed<StripePhonePlanProductItem[]>(() => {
  return (productPhonePlans.value?.products ?? [])
    .filter(
      (it) => it.currency.toString() === selectedCurrency.value.toString(),
    )
    .filter(
      (it) => it.schedule.toString() === selectedSchedule.value.toString(),
    );
});

const getPhonePrice = (price: StripePhonePlanProductItem | undefined) => {
  return (productPhoneNumbers.value?.products ?? []).filter(
    (it) =>
      it.currency.toString() === selectedCurrency.value.toString() &&
      it.schedule.toString() === selectedSchedule.value.toString() &&
      it.phoneCountry.toString() === price?.phoneCountry?.toString?.(),
  )?.[0]?.cost;
};

const hasPhonePlans = computed(() => {
  return (phonePlans.value?.length ?? 0) > 0;
});

const hasSubscriptionAndNoAddon = computed(() => {
  return subscription.value && !hasPhonePlans.value;
});

onMounted(async () => {
  await loadFetchMany(
    [
      () => store3.getProductPhonePlans(),
      () => store3.getProductPhoneNumbers(),
      () => store3.getPhonePlans(),
    ],
    store3,
    loading,
  );
});
</script>
